/**
 * lunaAlignment — which rows in a submitted batch followed Luna's advice.
 *
 * The portal is the only place that holds both halves of that question at the
 * same moment: Luna's bucket for a row, and the decision the reviewer just
 * made. The server turns each id we send into one "Luna recommendation
 * accepted" event, so this is deliberately conservative — a row is reported
 * only when its bucket unambiguously matches the submitted decision.
 *
 * Pure and UI-free, like reviewLogic: the repo's vitest setup runs in a `node`
 * environment with no jsdom, so everything testable lives outside the
 * components (see vitest.config.ts).
 */

export type LunaSuggestionGroup = {
  account_ids: string[];
  reasoning: string | null;
};

/** The shape `useGetAISuggestions` returns (and ReviewDetail's empty fallback). */
export type LunaSuggestions = {
  auto_approve: LunaSuggestionGroup[];
  auto_reject: LunaSuggestionGroup[];
  needs_review: LunaSuggestionGroup[];
};

export type LunaBucket = keyof LunaSuggestions;

const ALL_BUCKETS: LunaBucket[] = [
  "auto_approve",
  "auto_reject",
  "needs_review",
];

/**
 * The bucket a decision follows, or null when none does.
 *
 * Mirrors the display-side predicate in ReviewAccountsTable
 * (`isAffirmativeAction`): an approve card is followed by "required", a reject
 * card by "not_required". `needs_review` asks the reviewer to decide for
 * themselves, so no decision can "follow" it, and neither can "out_of_scope"
 * or "query" follow anything.
 */
export const acceptedBucketFor = (
  review: string | null | undefined,
): LunaBucket | null => {
  if (review === "required") return "auto_approve";
  if (review === "not_required") return "auto_reject";
  return null;
};

const idsIn = (groups: LunaSuggestionGroup[] | null | undefined): Set<string> =>
  new Set((groups ?? []).flatMap((group) => group?.account_ids ?? []));

/**
 * The subset of `accountIds` whose Luna suggestion aligns with `review`.
 *
 * Returns a de-duplicated list in the caller's order, and `[]` whenever there
 * is no suggestion data, no decision, or a decision nothing can align with. A
 * row listed in more than one bucket is dropped: Luna's advice for it is
 * self-contradictory, so no decision can be said to have accepted it.
 */
export const lunaSuggestedAccountIds = (
  suggestions: LunaSuggestions | null | undefined,
  accountIds: string[] | null | undefined,
  review: string | null | undefined,
): string[] => {
  const bucket = acceptedBucketFor(review);
  if (!bucket || !suggestions || !accountIds || accountIds.length === 0) {
    return [];
  }

  const aligned = idsIn(suggestions[bucket]);
  if (aligned.size === 0) return [];

  const contradicted = idsIn(
    ALL_BUCKETS.filter((key) => key !== bucket).flatMap(
      (key) => suggestions[key] ?? [],
    ),
  );

  const seen = new Set<string>();
  return accountIds.filter((id) => {
    if (!aligned.has(id) || contradicted.has(id) || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};
